// İSG/Süper Admin: birleşik belgeden sadece seçilen sayfaları (PDF) veya bölgeyi (resim) alıp yeni sürüm yapar
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Document, Page } from 'react-pdf';
import { Check, ChevronLeft, ChevronRight, Crop, Loader2, Scissors, Search, X, ZoomIn, ZoomOut } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZOOMS = [0.75, 1, 1.25, 1.5, 2, 2.5, 3];

const PageZoom = ({ url, count, page, setPage, picked, toggle, onClose }) => {
  const [z, setZ] = useState(2);
  const on = picked.includes(page);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl h-[94vh] flex flex-col" data-testid="split-zoom-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{page}. sayfa / {count}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-slate-100">
          <Button size="sm" variant="ghost" className="h-8 text-slate-100 hover:bg-white/10 hover:text-white" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="split-zoom-prev"><ChevronLeft className="h-4 w-4 mr-1" />Önceki</Button>
          <Button size="sm" variant="ghost" className="h-8 text-slate-100 hover:bg-white/10 hover:text-white" disabled={page >= count} onClick={() => setPage(page + 1)} data-testid="split-zoom-next">Sonraki<ChevronRight className="h-4 w-4 ml-1" /></Button>
          <span className="mx-1 h-5 w-px bg-white/20" />
          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-100 hover:bg-white/10 hover:text-white" disabled={z === ZOOMS[0]} onClick={() => setZ(ZOOMS[Math.max(ZOOMS.indexOf(z) - 1, 0)])} data-testid="split-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
          <span className="w-12 text-center text-xs tabular-nums" data-testid="split-zoom-level">{Math.round(z * 100)}%</span>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-100 hover:bg-white/10 hover:text-white" disabled={z === ZOOMS[ZOOMS.length - 1]} onClick={() => setZ(ZOOMS[Math.min(ZOOMS.indexOf(z) + 1, ZOOMS.length - 1)])} data-testid="split-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
          <Button size="sm" onClick={() => toggle(page)} data-testid="split-zoom-toggle"
            className={`ml-auto h-8 ${on ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}>
            {on ? <><X className="h-4 w-4 mr-1" />Seçimden Çıkar</> : <><Check className="h-4 w-4 mr-1" />Bu Sayfayı Al</>}
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-slate-100 p-3">
          <Document file={url} loading={<Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" />}>
            <div className={`mx-auto w-fit border-4 ${on ? 'border-emerald-500' : 'border-transparent'}`}>
              <Page pageNumber={page} width={420 * z} renderTextLayer={false} renderAnnotationLayer={false} />
            </div>
          </Document>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PdfPicker = ({ url, picked, setPicked }) => {
  const [count, setCount] = useState(0);
  const [zoom, setZoom] = useState(null);
  const toggle = (n) => setPicked(prev => (prev.includes(n) ? prev.filter(p => p !== n) : [...prev, n]));
  return (
    <>
    <Document file={url} onLoadSuccess={({ numPages }) => setCount(numPages)} loading={<Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" />}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="split-pdf-grid">
        {Array.from({ length: count }, (_, i) => i + 1).map(n => {
          const on = picked.includes(n);
          return (
            <div key={n} role="button" tabIndex={0} onClick={() => toggle(n)} onKeyDown={(e) => e.key === 'Enter' && toggle(n)} data-testid={`split-page-${n}`}
              className={`group relative cursor-pointer overflow-hidden rounded-lg border-2 bg-white text-left transition-[border-color,box-shadow,opacity] ${on ? 'border-emerald-500 shadow-lg ring-2 ring-emerald-200' : 'border-slate-200 opacity-70 hover:opacity-100 hover:border-sky-400'}`}>
              <Page pageNumber={n} width={210} renderTextLayer={false} renderAnnotationLayer={false} />
              <span className={`absolute left-2 top-2 flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-bold ${on ? 'bg-emerald-600 text-white' : 'bg-slate-800/70 text-white'}`}>{on ? <Check className="h-3.5 w-3.5" /> : n}</span>
              <button type="button" title="Büyüt" onClick={(e) => { e.stopPropagation(); setZoom(n); }} data-testid={`split-page-zoom-${n}`}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/75 text-white shadow transition-transform hover:scale-110 hover:bg-sky-600">
                <Search className="h-4 w-4" />
              </button>
              <p className={`border-t px-2 py-1 text-center text-xs font-semibold ${on ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500'}`}>{n}. sayfa{on ? ' · alınacak' : ''}</p>
            </div>
          );
        })}
      </div>
    </Document>
    {zoom && <PageZoom url={url} count={count} page={zoom} setPage={setZoom} picked={picked} toggle={toggle} onClose={() => setZoom(null)} />}
    </>
  );
};

const ImageCropper = ({ url, crop, setCrop }) => {
  const ref = useRef(null);
  const start = useRef(null);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1), y: Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1) };
  };
  const down = (e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); start.current = pos(e); setCrop(null); };
  const move = (e) => {
    if (!start.current) return;
    const p = pos(e), s = start.current;
    setCrop({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const up = () => { start.current = null; setCrop(c => (c && c.w > 0.02 && c.h > 0.02 ? c : null)); };
  return (
    <div className="flex justify-center">
      <div ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} className="relative inline-block cursor-crosshair select-none touch-none" data-testid="split-crop-area">
        <img src={url} alt="Belge" draggable={false} className="block max-h-[62vh] max-w-full" />
        {crop && (
          <div className="pointer-events-none absolute border-2 border-emerald-500" data-testid="split-crop-box"
            style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%`, boxShadow: '0 0 0 9999px rgba(15,23,42,0.55)' }} />
        )}
      </div>
    </div>
  );
};

export const SplitDocumentDialog = ({ open, onClose, url, contentType, entityType, entityId, docKey, fileId, docLabel, onDone }) => {
  const isPdf = contentType === 'application/pdf';
  const [picked, setPicked] = useState([]);
  const [crop, setCrop] = useState(null);
  const [busy, setBusy] = useState(false);
  const ready = isPdf ? picked.length > 0 : !!crop;
  const save = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/${entityType}/${entityId}/document-files/${docKey}/extract`, { file_id: fileId, ...(isPdf ? { pages: [...picked].sort((a, b) => a - b) } : { crop }) });
      toast.success(isPdf ? `Sadece ${[...picked].sort((a, b) => a - b).join(', ')}. sayfa alındı` : 'Seçilen bölge kırpıldı', { description: 'Yeni sürüm onay bekliyor; orijinal dosya Belge Geçmişi\'nde.' });
      onDone(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem yapılamadı'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-5xl max-h-[94vh] flex flex-col" data-testid="split-document-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{isPdf ? <Scissors className="h-5 w-5 text-sky-600" /> : <Crop className="h-5 w-5 text-sky-600" />}{isPdf ? 'Sayfa Ayır' : 'Kırp'} – {docLabel}</DialogTitle>
        </DialogHeader>
        <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          {isPdf ? 'Bu evraka ait sayfaları işaretleyin (okumak için büyüteç ikonuna basın). Seçilmeyen sayfalar yeni sürümden çıkarılır.' : 'Fareyle bu evraka ait bölgeyi çerçeve içine alın. Çerçeve dışı kesilir.'} Orijinal dosya Belge Geçmişi'nde kalır, yeni sürüm onay bekler.
        </p>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border bg-slate-100 p-3">
          {isPdf ? <PdfPicker url={url} picked={picked} setPicked={setPicked} /> : <ImageCropper url={url} crop={crop} setCrop={setCrop} />}
        </div>
        <DialogFooter className="items-center gap-2">
          <p className="mr-auto text-xs font-semibold text-slate-600" data-testid="split-selection-info">
            {isPdf ? (picked.length ? `Alınacak: ${[...picked].sort((a, b) => a - b).join(', ')}. sayfa` : 'Henüz sayfa seçilmedi') : (crop ? 'Bölge seçildi' : 'Henüz bölge seçilmedi')}
          </p>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="split-cancel-btn">Vazgeç</Button>
          <Button onClick={save} disabled={!ready || busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="split-save-btn">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Scissors className="mr-1 h-4 w-4" />}{isPdf ? 'Sadece Seçilenleri Al' : 'Kırp ve Kaydet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
